import React, { useState } from "react";
import {
  Modal,
  Button,
  Group,
  Text,
  Stack,
  NumberInput,
  Alert,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import moment from "moment";
import RemoteSelect2 from "./remoteSelect2";
import { useServerApi } from "../hooks/userServerApi";
import { useCellRender } from "../hooks/useCellRender";
import { useFormatter } from "../hooks/useFomatter";

const BankTransactionCommissionStatementLink = ({
  opened,
  onClose,
  bankTransaction,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [api] = useServerApi();
  const [cellRender] = useCellRender();
  const [formatter] = useFormatter();
  const [commissionStatementId, setCommissionStatementId] = useState(null);
  const [selectedStatement, setSelectedStatement] = useState(null);
  const [bankCharge, setBankCharge] = useState(bankTransaction?.bankCharge || 0);
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (opened && bankTransaction) {
      setBankCharge(bankTransaction.bankCharge || 0);
      setCommissionStatementId(null);
      setSelectedStatement(null);
    }
  }, [opened, bankTransaction]);

  if (!bankTransaction) return null;

  const netAmount = +(bankTransaction.deposit || 0).toFixed(2);
  const grossAmount = +(netAmount + (bankCharge || 0)).toFixed(2);

  const handleConfirm = async () => {
    if (!commissionStatementId) {
      toast.error(t("bankTransaction.linkCommissionStatement.selectRequired"));
      return;
    }
    try {
      setLoading(true);
      await api.BankTransaction.linkCommissionStatement({
        bankTransactionId: bankTransaction._id,
        commissionStatementId,
        bankCharge,
      });
      toast.success(t("bankTransaction.linkCommissionStatement.success"));
      onSuccess?.();
      onClose();
    } catch (error) {
      toast.error(error?.message || t("bankTransaction.linkCommissionStatement.error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t("bankTransaction.linkCommissionStatement.title")}
      size="md"
    >
      <Stack spacing="md">
        <Alert color="blue" variant="light">
          <Text size="sm" weight={600}>
            {bankTransaction.code}
          </Text>
          <Text size="xs" color="dimmed">
            {moment(bankTransaction.date).format("LL")} · {bankTransaction.detail}
          </Text>
        </Alert>

        <Group position="apart">
          <Text size="sm">{t("bankTransaction.linkCommissionStatement.netDeposit")}</Text>
          <Text size="sm" weight={600}>
            {cellRender.Currency(netAmount)}
          </Text>
        </Group>

        <NumberInput
          label={t("bankTransaction.bankCharge")}
          value={bankCharge}
          onChange={setBankCharge}
          precision={2}
          parser={formatter.currencyParser}
          formatter={formatter.currencyFormat}
          min={0}
        />

        <Group position="apart">
          <Text size="sm">{t("bankTransaction.linkCommissionStatement.grossAmount")}</Text>
          <Text size="sm" weight={600}>
            {cellRender.Currency(grossAmount)}
          </Text>
        </Group>

        <RemoteSelect2
          label={t("bankTransaction.linkCommissionStatement.selectStatement")}
          apiEntity="commissionStatement"
          value={commissionStatementId}
          valueField="_id"
          labelField="code"
          labelRender={(data) =>
            `${data.code} · ${data.period || ""} · ${formatter.currency(data.totalCommission || 0)}`
          }
          searchFields={["code", "period", "importRemark"]}
          preQuery={{
            status: { $ne: "CONFIRMED" },
            bankTransaction: { $exists: false },
          }}
          sort={{ by: "createdAt", order: "desc" }}
          onDataChange={(item) => {
            setCommissionStatementId(item?._id || null);
            setSelectedStatement(item);
          }}
          placeholder={t("bankTransaction.linkCommissionStatement.selectPlaceholder")}
        />

        {selectedStatement && (
          <Text size="xs" color="dimmed">
            {t("bankTransaction.linkCommissionStatement.statementTotal")}:{" "}
            {formatter.currency(selectedStatement.totalCommission || 0)}
          </Text>
        )}

        <Group position="right" mt="md">
          <Button variant="default" onClick={onClose}>
            {t("Cancel")}
          </Button>
          <Button loading={loading} onClick={handleConfirm}>
            {t("Confirm")}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default BankTransactionCommissionStatementLink;
